// Small Supabase Auth adapter shared with Survivor through the standard
// browser session key. Keeping this transport dependency-free lets the Idler
// build stay light while still participating in the same auth.users account.

function projectRef(url) {
  try { return new URL(url).hostname.split('.')[0]; } catch { return ''; }
}

function sessionStorageKey(url) {
  const ref = projectRef(url);
  return ref ? `sb-${ref}-auth-token` : '';
}

async function readJson(response) {
  return response.json().catch(() => null);
}

export class SharedAuth {
  constructor({ url = '', anonKey = '' } = {}) {
    this.url = url.replace(/\/$/, '');
    this.anonKey = anonKey;
    this.storageKey = sessionStorageKey(this.url);
    this.session = this.readStoredSession();
    this.listeners = new Set();
    window.addEventListener('storage', (event) => {
      if (event.key !== this.storageKey) return;
      this.session = this.readStoredSession();
      this.notify();
    });
  }

  get configured() { return Boolean(this.url && this.anonKey); }
  get accessToken() { return this.session?.access_token || null; }
  get user() { return this.session?.user || null; }
  get username() { return this.user?.user_metadata?.username || this.user?.email || 'account'; }

  onChange(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  readStoredSession() {
    if (!this.storageKey) return null;
    try {
      const stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null');
      return stored?.access_token ? stored : null;
    } catch {
      return null;
    }
  }

  writeSession(session) {
    this.session = session;
    if (this.storageKey) {
      if (session) localStorage.setItem(this.storageKey, JSON.stringify(session));
      else localStorage.removeItem(this.storageKey);
    }
    this.notify();
    return session;
  }

  notify() {
    this.listeners.forEach((listener) => listener(this.session));
  }

  async authRequest(path, options = {}) {
    if (!this.configured) throw new Error('Cloud accounts are not configured.');
    const response = await fetch(`${this.url}/auth/v1/${path}`, {
      ...options,
      headers: {
        apikey: this.anonKey,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    const payload = await readJson(response);
    if (!response.ok) throw new Error(payload?.msg || payload?.message || payload?.error_description || 'Authentication request failed.');
    return payload;
  }

  async restRequest(path, options = {}) {
    if (!this.configured) throw new Error('Cloud accounts are not configured.');
    const response = await fetch(`${this.url}/rest/v1/${path}`, {
      ...options,
      headers: {
        apikey: this.anonKey,
        Authorization: `Bearer ${this.accessToken || this.anonKey}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    const payload = await readJson(response);
    if (!response.ok) throw new Error(payload?.message || payload?.hint || 'Account profile request failed.');
    return payload;
  }

  async restore() {
    if (!this.session) return null;
    const expiresAt = Number(this.session.expires_at || 0) * 1000;
    if (expiresAt && expiresAt > Date.now() + 60_000) return this.session;
    if (!this.session.refresh_token) return this.writeSession(null);
    try {
      const refreshed = await this.authRequest('token?grant_type=refresh_token', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: this.session.refresh_token }),
      });
      return this.writeSession({ ...refreshed, user: refreshed.user || this.session.user });
    } catch {
      return this.writeSession(null);
    }
  }

  async resolveEmail(identifier) {
    if (identifier.includes('@')) return identifier;
    const result = await fetch(`${this.url}/rest/v1/rpc/email_for_username`, {
      method: 'POST',
      headers: { apikey: this.anonKey, Authorization: `Bearer ${this.anonKey}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ lookup_username: identifier }),
    });
    const email = await readJson(result);
    if (!result.ok || typeof email !== 'string' || !email) throw new Error('Incorrect username or password.');
    return email;
  }

  async ensureProfile(user, username = user?.user_metadata?.username) {
    if (!user?.id || !username) throw new Error('Missing profile information for this account.');
    const existing = await this.restRequest(`profiles?select=username&id=eq.${encodeURIComponent(user.id)}`);
    if (existing?.[0]?.username) return existing[0].username;
    await this.restRequest('profiles', {
      method: 'POST',
      headers: { Prefer: 'resolution=ignore-duplicates,return=minimal' },
      body: JSON.stringify({ id: user.id, username }),
    });
    return username;
  }

  async signIn(identifier, password) {
    try {
      const email = await this.resolveEmail(identifier.trim());
      const session = await this.authRequest('token?grant_type=password', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      const username = await this.ensureProfile(session.user, session.user?.user_metadata?.username || email);
      const user = { ...session.user, user_metadata: { ...(session.user?.user_metadata || {}), username } };
      return { ok: true, session: this.writeSession({ ...session, user }), username };
    } catch (error) {
      return { ok: false, error: error.message || 'Incorrect username or password.' };
    }
  }

  async signUp({ username, email, password }) {
    try {
      const session = await this.authRequest('signup', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim(), password, data: { username: username.trim() } }),
      });
      if (!session.access_token) return { ok: true, confirmed: false };
      const profileUsername = await this.ensureProfile(session.user, username.trim());
      const user = { ...session.user, user_metadata: { ...(session.user?.user_metadata || {}), username: profileUsername } };
      return { ok: true, confirmed: true, session: this.writeSession({ ...session, user }), username: profileUsername };
    } catch (error) {
      return { ok: false, error: error.message || 'Account creation failed.' };
    }
  }

  async signOut() {
    if (this.accessToken) {
      try {
        await this.authRequest('logout', { method: 'POST', headers: { Authorization: `Bearer ${this.accessToken}` } });
      } catch {
        // Local session removal is still the safe outcome if the network is down.
      }
    }
    this.writeSession(null);
  }
}

export function sharedAuth() {
  return new SharedAuth({
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  });
}
